import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import {
  X, MoreVertical, User, Mail, Phone, Building2, MapPin, Truck,
  CreditCard, FileText, Package, ChevronDown, ChevronUp, Download,
  ExternalLink, Loader2, CheckCircle2, Home, Ban, AlertTriangle, RotateCcw, Printer
} from 'lucide-react'
import { ShippingOrder, shipping, TrackingEventData } from '@/lib/api'
import { CarrierLogo, CountryFlag } from '@/components/CarrierLogos'
import { useToast } from '@/components/ui/Toast'
import LabelPrintModal from './LabelPrintModal'

interface ShipmentDetailModalProps {
  order: ShippingOrder
  onClose: () => void
}

// Configuración de estados para la timeline
const TIMELINE_STEPS = [
  { key: 'created', label: 'CREADO', icon: Package },
  { key: 'shipped', label: 'ENVIADO', icon: Truck },
  { key: 'in_transit', label: 'EN TRÁNSITO', icon: Truck },
  { key: 'out_for_delivery', label: 'EN REPARTO', icon: MapPin },
  { key: 'delivered', label: 'ENTREGADO', icon: Home },
]

// Mapeo de estados a índice de timeline (debe coincidir con TIMELINE_STEPS)
const STATUS_TO_INDEX: Record<string, number> = {
  pending: 0,           // No tiene paso propio, se muestra en "created"
  created: 0,           // CREADO (índice 0)
  picked_up: 1,         // RECOGIDO = ENVIADO (índice 1) - CTT usa este estado
  shipped: 1,           // ENVIADO (índice 1)
  in_transit: 2,        // EN TRÁNSITO (índice 2)
  out_for_delivery: 3,  // EN REPARTO (índice 3)
  ready_for_pickup: 3,  // EN PARCELSHOP: depositado, pendiente de que lo recoja el cliente
  delivered: 4,         // ENTREGADO (índice 4)
}

// Mapeo de estados de tracking a etiquetas legibles
const STATUS_LABELS: Record<string, string> = {
  pending: 'PENDIENTE',
  created: 'CREADO',
  picked_up: 'ENVIADO',  // CTT usa picked_up = Recogido, lo mostramos como Enviado
  shipped: 'ENVIADO',
  in_transit: 'EN TRÁNSITO',
  out_for_delivery: 'EN REPARTO',
  ready_for_pickup: 'EN PARCELSHOP',
  delivered: 'ENTREGADO',
  failed: 'FALLIDO',
  returned: 'DEVUELTO',
  cancelled: 'CANCELADO',
}

export default function ShipmentDetailModal({ order, onClose }: ShipmentDetailModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [showTrackingHistory, setShowTrackingHistory] = useState(false)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [revertToQueue, setRevertToQueue] = useState(true)
  const [forceCancel, setForceCancel] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [printModal, setPrintModal] = useState<{ pdfBlob: Blob } | null>(null)

  const shipment = order.shipment!
  const currentStatusIndex = STATUS_TO_INDEX[shipment.status] || 0

  // Query para obtener eventos de tracking reales
  const trackingEventsQuery = useQuery({
    queryKey: ['tracking-events', shipment.id],
    queryFn: () => shipping.getTrackingEvents(shipment.id),
    staleTime: 30000, // 30 segundos
  })

  // Transformar eventos de la API al formato de visualización
  const trackingEvents = (trackingEventsQuery.data?.events || []).map((event: TrackingEventData) => {
    const eventDate = event.status_date ? new Date(event.status_date) : new Date(event.created_at || '')
    return {
      date: eventDate.toLocaleDateString('es-ES'),
      time: eventDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      location: event.location || 'N/A',
      status: STATUS_LABELS[event.status] || event.status.toUpperCase(),
      detail: event.description || STATUS_LABELS[event.status] || event.status
    }
  })

  // Si no hay eventos, mostrar al menos el evento de creación
  if (trackingEvents.length === 0 && shipment.created_at) {
    trackingEvents.push({
      date: new Date(shipment.created_at).toLocaleDateString('es-ES'),
      time: new Date(shipment.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      location: 'N/A',
      status: 'CREADO',
      detail: 'Envío creado'
    })
  }

  // Mutations
  const downloadLabelMutation = useMutation({
    mutationFn: () => shipping.downloadLabel(shipment.id),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta_${shipment.tracking_number}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Etiqueta descargada')
    },
    onError: (error: any) => {
      toast.error('Error descargando etiqueta', error.message)
    },
  })

  const printLabelMutation = useMutation({
    mutationFn: () => shipping.downloadLabel(shipment.id),
    onSuccess: (blob) => {
      setPrintModal({ pdfBlob: blob })
    },
    onError: (error: any) => {
      toast.error('Error obteniendo etiqueta', error.message)
    },
  })

  const cancelShipmentMutation = useMutation({
    mutationFn: () => shipping.cancelShipment(shipment.id, revertToQueue, forceCancel),
    onSuccess: (data) => {
      setShowCancelConfirm(false)
      setCancelError(null)
      setForceCancel(false)
      if (data.warning) {
        toast.info(data.message, data.warning)
      } else {
        toast.success(data.message)
      }
      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: ['shipping-tracking'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-orders'] })
      onClose()
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message
      setCancelError(errorMsg)
      // No cerramos el modal para permitir forzar la cancelación
      toast.error('Error cancelando envío', errorMsg)
    },
  })

  // Verificar si el envío puede cancelarse
  const canCancel = !['delivered', 'cancelled'].includes(shipment.status)

  // Obtener nombre de servicio según transportista
  const getCarrierService = () => {
    const services: Record<string, string> = {
      mrw: 'MRW Entrega En 1 Día Laborable',
      gls: 'GLS Entrega 24-48h',
      ctt: 'CTT Entrega En 1 Día Laborable',
    }
    return services[shipment.carrier_code] || 'Servicio estándar'
  }

  // Status badge colors
  const getStatusColor = () => {
    const colors: Record<string, string> = {
      pending: 'bg-slate-100 text-slate-700',
      created: 'bg-blue-100 text-blue-700',
      picked_up: 'bg-indigo-100 text-indigo-700',  // Mismo color que shipped
      shipped: 'bg-indigo-100 text-indigo-700',
      in_transit: 'bg-teal-100 text-teal-700',
      out_for_delivery: 'bg-amber-100 text-amber-700',
      ready_for_pickup: 'bg-violet-100 text-violet-700',
      delivered: 'bg-lime-100 text-lime-800',
      failed: 'bg-red-100 text-red-700',
      returned: 'bg-orange-100 text-orange-700',
    }
    return colors[shipment.status] || colors.pending
  }

  const getStatusLabel = () => {
    const labels: Record<string, string> = {
      pending: 'PENDIENTE',
      created: 'CREADO',
      picked_up: 'ENVIADO',  // CTT: Recogido = Enviado
      shipped: 'ENVIADO',
      in_transit: 'EN TRÁNSITO',
      out_for_delivery: 'EN REPARTO',
      ready_for_pickup: 'EN PARCELSHOP',
      delivered: 'ENTREGADO',
      failed: 'FALLIDO',
      returned: 'DEVUELTO',
    }
    return labels[shipment.status] || 'DESCONOCIDO'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-10 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl my-10 overflow-hidden">
        {/* Header con datos del envío */}
        <div className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              {/* Logo transportista */}
              <CarrierLogo carrier={shipment.carrier_code} className="h-6 w-20" />

              {/* Destino */}
              <div className="flex items-center gap-2">
                <CountryFlag countryCode={order.shipping_address.country_code} />
                <span className="text-slate-600 dark:text-slate-400">{order.shipping_address.country_code}</span>
              </div>

              {/* Pedido */}
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Pedido</div>
                <div className="font-semibold text-slate-900 dark:text-white">{order.reference}</div>
              </div>

              {/* Tracking */}
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Tracking</div>
                <div className="font-mono text-sm text-slate-700 dark:text-slate-300">{shipment.tracking_number}</div>
              </div>

              {/* Estado */}
              <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${getStatusColor()}`}>
                {getStatusLabel()}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Menú de opciones */}
              <div className="relative">
                <button
                  onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {showOptionsMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowOptionsMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 py-1">
                      {/* Descargar etiqueta */}
                      <button
                        onClick={() => {
                          downloadLabelMutation.mutate()
                          setShowOptionsMenu(false)
                        }}
                        disabled={downloadLabelMutation.isPending}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <Download className="w-4 h-4" />
                        Descargar etiqueta
                      </button>


                      {/* Cancelar envío */}
                      {canCancel && (
                        <button
                          onClick={() => {
                            setShowOptionsMenu(false)
                            setShowCancelConfirm(true)
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Ban className="w-4 h-4" />
                          Cancelar envío
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={onClose}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {/* Banner de estado */}
          <div className={`mb-6 px-4 py-2 rounded-lg ${getStatusColor()}`}>
            <span className="font-semibold">{getStatusLabel()}</span>
          </div>

          {/* Timeline de progreso */}
          <div className="mb-8">
            <div className="flex items-center justify-between relative">
              {/* Línea de fondo */}
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-700" />
              {/* Línea de progreso */}
              <div
                className="absolute top-5 left-0 h-0.5 bg-teal-500 transition-all duration-500"
                style={{ width: `${(currentStatusIndex / (TIMELINE_STEPS.length - 1)) * 100}%` }}
              />

              {TIMELINE_STEPS.map((step, index) => {
                const Icon = step.icon
                const isCompleted = index < currentStatusIndex
                const isCurrent = index === currentStatusIndex

                return (
                  <div key={step.key} className="flex flex-col items-center relative z-10">
                    {/* Icono */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                        isCompleted || isCurrent
                          ? 'bg-teal-500 border-teal-500 text-white'
                          : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>

                    {/* Label */}
                    <div className="mt-2 text-center">
                      <div className={`text-xs font-medium ${
                        isCompleted || isCurrent ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'
                      }`}>
                        {step.label}
                      </div>
                      {isCurrent && shipment.shipped_at && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {new Date(shipment.shipped_at).toLocaleDateString('es-ES')}{' '}
                          {new Date(shipment.shipped_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Banner con tracking del transportista (expandible) */}
          <div className="mb-6">
            <button
              onClick={() => setShowTrackingHistory(!showTrackingHistory)}
              className="w-full flex items-center justify-between px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <span className="font-medium">
                {shipment.carrier_code.toUpperCase()} - {getCarrierService()}: {shipment.tracking_number}
              </span>
              {showTrackingHistory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {/* Historial de tracking */}
            {showTrackingHistory && (
              <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="grid grid-cols-4 gap-4 px-4 py-2 bg-slate-50 dark:bg-slate-900/50 text-xs font-medium text-teal-600 uppercase">
                  <div>Fecha y hora</div>
                  <div>Localización</div>
                  <div>Estado</div>
                  <div>Detalle</div>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {trackingEvents.map((event, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-4 px-4 py-3 text-sm">
                      <div className="text-slate-600 dark:text-slate-400">
                        <div>{event.date}</div>
                        <div>{event.time}</div>
                      </div>
                      <div className="text-slate-600 dark:text-slate-400">{event.location}</div>
                      <div className="text-slate-700 dark:text-slate-300">{event.status}</div>
                      <div className="text-slate-600 dark:text-slate-400">{event.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Datos de entrega y pago */}
          <div className="grid grid-cols-2 gap-6">
            {/* DATOS DE ENTREGA */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
                Datos de Entrega
              </h3>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-slate-400 mt-1" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{order.shipping_address.name}</span>
                </div>

                {order.shipping_address.company && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-slate-400 mt-1" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{order.shipping_address.company}</span>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-slate-400 mt-1" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{order.customer_email}</span>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-slate-400 mt-1" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{order.shipping_address.phone || '-'}</span>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                  <div className="text-sm text-slate-700 dark:text-slate-300 space-y-0.5">
                    <div>{order.shipping_address.street}</div>
                    {order.shipping_address.street2 && <div>{order.shipping_address.street2}</div>}
                    <div>{order.shipping_address.postal_code} {order.shipping_address.city}</div>
                    <div className="flex items-center gap-1">
                      <CountryFlag countryCode={order.shipping_address.country_code} className="w-4 h-3" />
                      {order.shipping_address.country_code}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Truck className="w-4 h-4 text-slate-400 mt-1" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{getCarrierService()}</span>
                </div>
              </div>

              <button className="text-sm text-teal-600 dark:text-teal-400 hover:underline">
                Cambiar dirección de facturación ▼
              </button>
            </div>

            {/* DATOS DE PAGO + ALMACÉN */}
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
                  Datos de Pago
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">F{order.id}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{order.payment_method}</span>
                  </div>
                  {shipment.cod_amount_sent && shipment.cod_amount_sent > 0 && (
                    <div className="flex items-center gap-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <CreditCard className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                        Reembolso etiqueta: {shipment.cod_amount_sent.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
                  Almacén
                </h3>
                <div className="text-sm text-slate-700 dark:text-slate-300">Default Warehouse</div>
              </div>

              {/* Acciones */}
              <div className="pt-4 space-y-3">
                <button
                  onClick={() => printLabelMutation.mutate()}
                  disabled={printLabelMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
                >
                  {printLabelMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Printer className="w-4 h-4" />
                  )}
                  Imprimir etiqueta
                </button>

                <button
                  onClick={() => downloadLabelMutation.mutate()}
                  disabled={downloadLabelMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  {downloadLabelMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Descargar etiqueta
                </button>

                {shipment.tracking_url && (
                  <a
                    href={shipment.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver tracking del transportista
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* PRODUCTOS */}
          <div className="mt-8 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
              Productos
            </h3>

            <div className="flex items-start gap-4">
              <div className="flex-1 space-y-3">
                {order.products && order.products.length > 0 ? (
                  order.products.map((product, index) => (
                    <div key={index} className="flex items-start gap-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      {/* Imagen del producto */}
                      <div className="w-16 h-16 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                              e.currentTarget.parentElement!.innerHTML = '<svg class="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>'
                            }}
                          />
                        ) : (
                          <Package className="w-6 h-6 text-slate-300 dark:text-slate-500" />
                        )}
                      </div>

                      {/* Info del producto */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 dark:text-white text-sm leading-tight line-clamp-2">
                          {product.name}
                        </h4>
                        {product.reference && (
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                            Ref: {product.reference}
                          </p>
                        )}
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">
                          €{product.price.toFixed(2)} x {product.quantity}
                        </p>
                      </div>

                      {/* Total */}
                      <div className="text-right flex-shrink-0">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">
                          €{product.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <div className="w-16 h-16 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center">
                      <Package className="w-6 h-6 text-slate-300 dark:text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">
                        Pedido #{order.reference}
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {order.items_count} artículo{order.items_count > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="w-48 bg-slate-800 text-white rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">PRECIO ENVÍO</span>
                  <span>€{(order.total_shipping || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">IVA</span>
                  <span>€{(order.total * 0.21 / 1.21).toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-700 pt-2 flex justify-between font-semibold">
                  <span>VALOR TOTAL</span>
                  <span>€{order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmación de cancelación */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Cancelar envío
              </h3>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              <p className="text-slate-600 dark:text-slate-400">
                ¿Estás seguro de que quieres cancelar este envío?
              </p>

              {/* Info del transportista */}
              <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CarrierLogo carrier={shipment.carrier_code} className="h-4 w-12" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {shipment.tracking_number}
                  </span>
                </div>

                {['mrw', 'gls', 'ctt', 'cex', 'fedex'].includes(shipment.carrier_code.toLowerCase()) && (
                  <div className="flex items-start gap-2 text-xs text-slate-500">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-teal-500" />
                    <span>
                      {shipment.carrier_code.toLowerCase() === 'cex' ? (
                        <>
                          Correos Express no permite anular envíos: se solicitará una parada
                          en vuelo, que solo aceptan si el envío está en reparto, con reparto
                          fallido o en nuevo reparto.
                        </>
                      ) : shipment.carrier_code.toLowerCase() === 'fedex' ? (
                        <>
                          FedEx cancela el envío completo (todos los bultos) si aún no se ha
                          entregado al mensajero. Una etiqueta creada y no cancelada se factura.
                        </>
                      ) : (
                        <>
                          Se enviará solicitud de cancelación al transportista.
                          Si el envío ya está en proceso, puede que no se pueda cancelar.
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Opción de revertir a cola */}
              <label className="flex items-start gap-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={revertToQueue}
                  onChange={(e) => setRevertToQueue(e.target.checked)}
                  className="mt-1 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                    <RotateCcw className="w-4 h-4" />
                    Devolver a cola de empaquetado
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    El pedido volverá a la cola para poder modificar paquetes,
                    reembolso, etc. y crear un nuevo envío.
                  </p>
                </div>
              </label>

              {/* Error de cancelación y opción de forzar */}
              {cancelError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{cancelError}</span>
                  </div>
                  <label className="flex items-start gap-3 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={forceCancel}
                      onChange={(e) => setForceCancel(e.target.checked)}
                      className="mt-1 rounded border-red-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-red-700 dark:text-red-400">
                        Forzar cancelación local
                      </div>
                      <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                        Cancela el envío en nuestro sistema aunque el transportista no lo permita.
                        La etiqueta puede seguir activa en el transportista.
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelConfirm(false)
                  setCancelError(null)
                  setForceCancel(false)
                }}
                disabled={cancelShipmentMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                No, mantener envío
              </button>
              <button
                onClick={() => cancelShipmentMutation.mutate()}
                disabled={cancelShipmentMutation.isPending}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 ${
                  forceCancel
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {cancelShipmentMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4" />
                )}
                {forceCancel ? 'Forzar cancelación' : 'Sí, cancelar envío'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de impresión */}
      {printModal && (
        <LabelPrintModal
          pdfBlob={printModal.pdfBlob}
          trackingNumber={shipment.tracking_number}
          onClose={() => setPrintModal(null)}
        />
      )}
    </div>
  )
}
